import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatMoney } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Checkbox, Field, Input, Select, Textarea, useToast } from '../components/ui';
import { EvidencePicker, HISTORICAL_EVIDENCE, useEvidenceUpload } from '../components/EvidenceFiles';
import { useRefusal } from './PartnerForm';
import { FindingText, formatMonth, type HistoricalRecord, type OverlapFinding, type RecordType } from './HistoricalFinance';

/**
 * The historical month's forms (Finance Phase 1, 2026-10-28) — each a page:
 * entering or editing a draft, finalizing it after the overlap review, correcting
 * a finalized one with its reason, voiding one.
 */

interface Currency {
  code: string;
  isBase: boolean;
  isActive: boolean;
  rateToBase: number | null;
}

function useCurrencies() {
  const rows = useQuery({ queryKey: ['currencies'], queryFn: () => api.get<Currency[]>('/currencies') }).data ?? [];
  return { rows, base: rows.find((c) => c.isBase)?.code ?? '' };
}

/** Routes: `/historical/new` (`?type=`), `/historical/:id/edit`. */
export function HistoricalRecordFormPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const editing = !!id;
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('historical');
  const upload = useEvidenceUpload(HISTORICAL_EVIDENCE);
  const { rows: currencies, base } = useCurrencies();
  // One per form: sent twice, the month is created once.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const existing = useQuery({
    queryKey: ['historical', id],
    queryFn: () => api.get<HistoricalRecord>(`/historical-records/${id}`),
    enabled: editing,
  });

  const [form, setForm] = useState({
    type: (params.get('type') as RecordType | null) ?? 'HISTORICAL_SALARIES',
    month: '',
    totalAmount: '',
    currency: '',
    exchangeRate: '',
    notes: '',
    source: '',
    externalReference: '',
  });
  const [duplicate, setDuplicate] = useState<string | null>(null);

  useEffect(() => {
    const r = existing.data;
    if (r)
      setForm({
        type: r.type,
        month: r.periodMonth.slice(0, 7),
        totalAmount: r.totalAmount,
        currency: r.currency,
        exchangeRate: r.exchangeRate ?? '',
        notes: r.notes ?? '',
        source: r.source ?? '',
        externalReference: r.externalReference ?? '',
      });
  }, [existing.data]);

  const currency = form.currency || base;
  const foreign = !!currency && !!base && currency !== base;
  const todayRate = currencies.find((c) => c.code === currency)?.rateToBase;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        type: form.type,
        month: form.month,
        totalAmount: Number(form.totalAmount),
        currency,
        exchangeRate: foreign && form.exchangeRate ? Number(form.exchangeRate) : undefined,
        notes: form.notes || (editing ? null : undefined),
        source: form.source || (editing ? null : undefined),
        externalReference: form.externalReference || (editing ? null : undefined),
      };
      return editing
        ? api.patch<HistoricalRecord>(`/historical-records/${id}`, body)
        : api.post<HistoricalRecord>('/historical-records', { ...body, fileIds: upload.ids, idempotencyKey });
    },
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ['historical'] });
      navigate(`/historical/${record.id}`);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'DUPLICATE_RESOURCE') {
        setDuplicate((e.details as { recordId?: string } | undefined)?.recordId ?? null);
      }
      toast.error(refusal(e));
    },
  });

  const ready = /^\d{4}-\d{2}$/.test(form.month) && Number(form.totalAmount) > 0 && !!currency && !upload.busy;
  const thisMonth = new Date().toISOString().slice(0, 7);

  return (
    <FormPage
      title={editing ? t('historical.edit') : t(form.type === 'HISTORICAL_SALARIES' ? 'historical.newSalaries' : 'historical.newExpenses')}
      subtitle={t('historical.paidHint')}
      backTo={editing ? `/historical/${id}` : `/historical?tab=${form.type === 'HISTORICAL_SALARIES' ? 'salaries' : 'expenses'}`}
      backLabel={t('historical.title')}
      submitLabel={editing ? t('common.save') : t('common.create')}
      submitting={save.isPending}
      disabled={!ready}
      loading={editing && existing.isLoading}
      error={existing.error}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={t('historical.type')} required>
          <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as RecordType })}>
            <option value="HISTORICAL_SALARIES">{t('historical.HISTORICAL_SALARIES')}</option>
            <option value="HISTORICAL_EXPENSES">{t('historical.HISTORICAL_EXPENSES')}</option>
          </Select>
        </Field>
        <Field label={t('historical.month')} required hint={form.month ? formatMonth(form.month, locale) : t('historical.monthHint')}>
          <Input type="month" max={thisMonth} value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} autoFocus />
        </Field>
        <Field label={t('historical.total')} required>
          <Input type="number" min={0.01} step="0.01" dir="ltr" value={form.totalAmount} onChange={(e) => setForm({ ...form, totalAmount: e.target.value })} />
        </Field>
        <Field label={t('common.currency')} required>
          <Select value={currency} onChange={(e) => setForm({ ...form, currency: e.target.value, exchangeRate: '' })}>
            {currencies.filter((c) => c.isActive).map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </Select>
        </Field>
        {foreign && (
          <Field
            label={t('historical.rate', { base })}
            hint={todayRate ? t('historical.rateHint', { rate: String(todayRate) }) : t('historical.rateHintNone')}
          >
            <Input type="number" min={0} step="0.00000001" dir="ltr" value={form.exchangeRate} onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })} />
          </Field>
        )}
        {foreign && Number(form.exchangeRate) > 0 && Number(form.totalAmount) > 0 && (
          <Field label={t('historical.base', { base })}>
            <p className="h-10 py-2 tabular">{formatMoney(Number(form.totalAmount) * Number(form.exchangeRate), base, locale)}</p>
          </Field>
        )}
        <Field label={t('historical.source')} hint={t('historical.sourceHint')}>
          <Input dir="auto" value={form.source} maxLength={200} onChange={(e) => setForm({ ...form, source: e.target.value })} />
        </Field>
        <Field label={t('historical.reference')}>
          <Input dir="auto" value={form.externalReference} maxLength={200} onChange={(e) => setForm({ ...form, externalReference: e.target.value })} />
        </Field>
        <Field label={t('common.notes')} className="sm:col-span-2">
          <Textarea dir="auto" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
        {!editing && (
          <Field label={t('historical.files')} hint={t('historical.filesHint')} className="sm:col-span-2">
            <EvidencePicker upload={upload} policy={HISTORICAL_EVIDENCE} />
          </Field>
        )}
      </FormGrid>
      {duplicate && (
        <p className="mt-4 text-body-sm">
          <button type="button" className="text-primary underline" onClick={() => navigate(`/historical/${duplicate}`)}>
            {t('historical.error.DUPLICATE_RESOURCE')}
          </button>
        </p>
      )}
    </FormPage>
  );
}

/** The overlap review on the finalize and correct pages: what was found, and the decision. */
function OverlapReview({
  findings,
  decided,
  setDecided,
  note,
  setNote,
}: {
  findings: OverlapFinding[];
  decided: boolean;
  setDecided: (v: boolean) => void;
  note: string;
  setNote: (v: string) => void;
}) {
  const { t } = useI18n();
  if (findings.length === 0) return <p className="text-body-sm text-muted">{t('historical.overlapNone')}</p>;
  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-body-sm font-medium text-[var(--text-primary)]">{t('historical.overlap')}</p>
        <ul className="list-disc space-y-1 ps-5 text-body-sm">
          {findings.map((f, i) => (
            <li key={i}><FindingText finding={f} /></li>
          ))}
        </ul>
        <p className="mt-2 text-caption text-muted">{t('historical.overlapIntro')}</p>
      </div>
      <Checkbox label={t('historical.decision')} checked={decided} onChange={(e) => setDecided(e.target.checked)} />
      <Field label={t('historical.decisionNote')} required hint={t('historical.decisionNoteHint')}>
        <Textarea dir="auto" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </div>
  );
}

function useRecord(id: string | undefined) {
  return useQuery({ queryKey: ['historical', id], queryFn: () => api.get<HistoricalRecord>(`/historical-records/${id}`) });
}

function RecordSummary({ record }: { record: HistoricalRecord }) {
  const { t, locale } = useI18n();
  return (
    <p className="text-body-sm">
      {t(`historical.${record.type}` as TranslationKey)} · {formatMonth(record.periodMonth, locale)} ·{' '}
      <span className="tabular">{formatMoney(record.totalAmount, record.currency, locale)}</span>
      {record.baseAmount && record.currency !== record.baseCurrency && (
        <span className="tabular text-muted"> ({formatMoney(record.baseAmount, record.baseCurrency ?? '', locale)})</span>
      )}
    </p>
  );
}

/** Route: `/historical/:id/finalize`. */
export function HistoricalFinalizePage() {
  const { id } = useParams<{ id: string }>();
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('historical');
  const record = useRecord(id);
  const [decided, setDecided] = useState(false);
  const [note, setNote] = useState('');

  const finalize = useMutation({
    mutationFn: () =>
      api.post(`/historical-records/${id}/finalize`, (record.data?.overlap.length ?? 0) > 0 ? { decision: 'EXCLUDES_RECORDED', note } : {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['historical'] });
      navigate(`/historical/${id}`);
    },
    onError: (e) => {
      void record.refetch();
      toast.error(refusal(e));
    },
  });

  const data = record.data;
  const findings = data?.overlap ?? [];
  const pastCutover = findings.some((f) => f.kind === 'AFTER_CUTOVER');
  const allowed = !pastCutover || can(P.HISTORICAL_FINANCE_CORRECT);
  const needsRate = !!data && !data.exchangeRate && data.currency !== data.baseCurrencyNow;

  return (
    <FormPage
      title={t('historical.finalizeTitle', { month: formatMonth(data?.periodMonth, locale) })}
      subtitle={t('historical.finalizeHint')}
      backTo={`/historical/${id}`}
      backLabel={formatMonth(data?.periodMonth, locale)}
      submitLabel={t('historical.finalize')}
      submitting={finalize.isPending}
      disabled={!data || data.status !== 'DRAFT' || !allowed || needsRate || (findings.length > 0 && (!decided || !note.trim()))}
      loading={record.isLoading}
      error={record.error}
      onSubmit={() => finalize.mutate()}
      summary={data && <RecordSummary record={data} />}
    >
      {needsRate && <p className="mb-4 text-body-sm text-warning-fg dark:text-warning-dark">{t('historical.rateHintNone')}</p>}
      <OverlapReview findings={findings} decided={decided} setDecided={setDecided} note={note} setNote={setNote} />
    </FormPage>
  );
}

/** Route: `/historical/:id/correct`. */
export function HistoricalCorrectPage() {
  const { id } = useParams<{ id: string }>();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('historical');
  const record = useRecord(id);
  const [form, setForm] = useState({ totalAmount: '', exchangeRate: '', reason: '' });
  const [decided, setDecided] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (record.data) setForm((f) => ({ ...f, totalAmount: f.totalAmount || record.data!.totalAmount, exchangeRate: f.exchangeRate || (record.data!.exchangeRate ?? '') }));
  }, [record.data]);

  const data = record.data;
  const findings = data?.overlap ?? [];
  const foreign = !!data && data.currency !== data.baseCurrencyNow;

  const correct = useMutation({
    mutationFn: () =>
      api.post(`/historical-records/${id}/correct`, {
        totalAmount: Number(form.totalAmount),
        exchangeRate: foreign && form.exchangeRate ? Number(form.exchangeRate) : undefined,
        reason: form.reason,
        ...(findings.length > 0 ? { decision: 'EXCLUDES_RECORDED', note } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['historical'] });
      void queryClient.invalidateQueries({ queryKey: ['financial-setup'] });
      navigate(`/historical/${id}`);
    },
    onError: (e) => toast.error(refusal(e)),
  });

  return (
    <FormPage
      title={t('historical.correctTitle', { month: formatMonth(data?.periodMonth, locale) })}
      subtitle={t('historical.correctHint')}
      backTo={`/historical/${id}`}
      backLabel={formatMonth(data?.periodMonth, locale)}
      submitLabel={t('historical.correct')}
      submitting={correct.isPending}
      disabled={
        !data ||
        Number(form.totalAmount) <= 0 ||
        (foreign && !(Number(form.exchangeRate) > 0)) ||
        !form.reason.trim() ||
        (findings.length > 0 && (!decided || !note.trim()))
      }
      loading={record.isLoading}
      error={record.error}
      onSubmit={() => correct.mutate()}
      summary={data && <RecordSummary record={data} />}
    >
      <FormGrid>
        <Field label={t('historical.total')} required>
          <Input type="number" min={0.01} step="0.01" dir="ltr" value={form.totalAmount} onChange={(e) => setForm({ ...form, totalAmount: e.target.value })} autoFocus />
        </Field>
        {foreign && (
          <Field label={t('historical.rate', { base: data?.baseCurrencyNow ?? '' })} required>
            <Input type="number" min={0} step="0.00000001" dir="ltr" value={form.exchangeRate} onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })} />
          </Field>
        )}
        <Field label={t('common.reason')} required className="sm:col-span-2">
          <Textarea dir="auto" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Field>
      </FormGrid>
      <div className="mt-6">
        <OverlapReview findings={findings} decided={decided} setDecided={setDecided} note={note} setNote={setNote} />
      </div>
    </FormPage>
  );
}

/** Route: `/historical/:id/void`. */
export function HistoricalVoidPage() {
  const { id } = useParams<{ id: string }>();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('historical');
  const record = useRecord(id);
  const [reason, setReason] = useState('');

  const doVoid = useMutation({
    mutationFn: () => api.post(`/historical-records/${id}/void`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['historical'] });
      navigate(`/historical/${id}`);
    },
    onError: (e) => toast.error(refusal(e)),
  });

  const data = record.data;
  return (
    <FormPage
      title={t('historical.voidTitle', { month: formatMonth(data?.periodMonth, locale) })}
      subtitle={t('historical.voidHint')}
      backTo={`/historical/${id}`}
      backLabel={formatMonth(data?.periodMonth, locale)}
      submitLabel={t('historical.void')}
      destructive
      submitting={doVoid.isPending}
      disabled={!reason.trim()}
      loading={record.isLoading}
      error={record.error}
      onSubmit={() => doVoid.mutate()}
      summary={data && <RecordSummary record={data} />}
    >
      <Field label={t('common.reason')} required>
        <Textarea dir="auto" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </FormPage>
  );
}
